import { useEffect, useRef, useState } from 'preact/hooks';
import { useStore } from '../hooks/useStore.js';
import './editor.css';

export interface EditorProps {
  path: string | null;
  onClose: () => void;
}

/**
 * Raw markdown editor.
 *
 * A plain textarea, deliberately. A real code editor (CodeMirror) would add
 * ~120KB to a bundle that must cold-start on a phone, and would fight the iOS
 * keyboard — while the thing being edited is a dozen lines of frontmatter. If
 * syntax highlighting is ever worth that, it can be swapped in behind this
 * same component.
 *
 * Edits go through the same optimistic store as everything else, so what you
 * type appears in the widgets behind the drawer as you type.
 */
export function Editor({ path, onClose }: EditorProps) {
  const store = useStore();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [draft, setDraft] = useState('');
  const [dirty, setDirty] = useState(false);

  const content = path ? store.get(path) : undefined;

  // Reset the draft whenever a different file is opened.
  useEffect(() => {
    setDraft(content ?? '');
    setDirty(false);
    if (path) textareaRef.current?.focus();
  }, [path]);

  /*
   * Lock the page behind the drawer. Without this the dashboard scrolls under
   * the scrim on touch, which reads as two screens fighting each other. iOS
   * needs `position: fixed` rather than `overflow: hidden` alone, so the scroll
   * offset is captured and restored to avoid jumping to the top on close.
   */
  useEffect(() => {
    if (!path) return;
    const { body } = document;
    const scrollY = window.scrollY;
    const previous = body.style.cssText;

    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.width = '100%';

    return () => {
      body.style.cssText = previous;
      window.scrollTo(0, scrollY);
    };
  }, [path]);

  useEffect(() => {
    if (!path) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      if ((event.metaKey || event.ctrlKey) && event.key === 's') {
        event.preventDefault();
        save();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  });

  if (!path) return null;

  const save = () => {
    if (!dirty) return;
    store.mutate({ kind: 'writeFile', path, content: draft });
    setDirty(false);
  };

  const closeAndSave = () => {
    save();
    onClose();
  };

  return (
    <div class="hud-editor" role="dialog" aria-modal="true" aria-label={`Editing ${path}`}>
      <div class="hud-editor__scrim" onClick={closeAndSave} />
      <div class="hud-editor__panel">
        <header class="hud-editor__head">
          <span class="hud-editor__path">{path}</span>
          <span class="hud-editor__actions">
            {dirty ? <span class="hud-editor__dirty">unsaved</span> : null}
            <button type="button" onClick={save} disabled={!dirty}>
              Save
            </button>
            <button type="button" onClick={closeAndSave}>
              Close
            </button>
          </span>
        </header>

        <textarea
          ref={textareaRef}
          class="hud-editor__text"
          value={draft}
          spellcheck={false}
          autocapitalize="off"
          autocomplete="off"
          onInput={(event) => {
            setDraft((event.target as HTMLTextAreaElement).value);
            setDirty(true);
          }}
        />

        <footer class="hud-editor__foot">
          Frontmatter between <code>---</code> fences is the config; everything below is yours.
          <kbd>Esc</kbd> closes, <kbd>Ctrl/Cmd+S</kbd> saves.
        </footer>
      </div>
    </div>
  );
}
